import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

// ===== 型定義 =====

/** アップロードの用途 */
export type UploadPurpose =
  | 'project_message'
  | 'shot_comment'
  | 'dm_client'
  | 'dm_specialist'
  // グループチャット（ChatThread）のメッセージ添付ファイル。
  // register で ChatMessageFile を作成し、file_key をメッセージ POST の file_keys に渡す。
  | 'chat_message'
  | 'avatar'
  | 'media_item'
  // マイサイト作品メディア（音源/サムネ画像）。ユーザースコープで S3 にアップロードし、
  // 返却された file_key を作品 CRUD API の image_key / audio_keys に渡す（register は行わない）。
  | 'work_media'
  // プロジェクトバナー画像。プロジェクトスコープで S3 にアップロードし、
  // 返却された file_key を PATCH /api/me/projects/<id> の banner_image_key に渡す（register は行わない）。
  | 'project_banner'
  // 共有ノートのイメージボードページの画像。セクションスコープで S3 にアップロードし、
  // 返却された file_key を POST /api/notes/<note_id>/board/items の image_key に渡す（register は行わない）。
  | 'note_board'
  // ノート（Notes）に埋め込む画像。ユーザースコープで S3 にアップロードし、
  // URL を Markdown 画像記法でノート本文に直接埋め込む（register は行わない）。
  | 'note'
  // 共有ライブラリのファイル。セクションスコープで S3 にアップロードし、
  // 返却された file_key を POST /api/library/files で登録する（register は行わない）。
  | 'shared_library'
  // /tools 配下ツールのアセット（例: ステップ見積もりのサムネイル）。
  // ユーザースコープで S3 にアップロードし、URL をツールプリセット payload に
  // 直接保持する（register は行わない）。
  | 'tool_asset'
  // 発行書類（御見積書 等）の印影＝ロゴ・社印・サイン。ユーザースコープで
  // S3 にアップロードし、返却された file_key を PATCH /api/me の
  // document_*_key に渡す（register は行わない）。
  | 'user_asset'
  // タスクボードのセルへ貼り付け・ドロップしたファイル。プロジェクトスコープで
  // S3 にアップロードし、URL を Markdown（画像は `![](url)`、それ以外は
  // `[📎 名前](url)`）でセル本文へ直接埋め込む（register は行わない）。
  | 'task_board'
  // soremon（AI アシスタント）のチャットへの添付。ユーザースコープで S3 に
  // アップロードし、返却された file_key を POST /api/soremon/messages の
  // attachments に渡す（register は行わない）。
  | 'soremon_attachment';

/** アップロード開始リクエストパラメータ */
export interface InitiateUploadParams {
  file_name: string;
  file_size: number;
  content_type: string;
  purpose: UploadPurpose;
  project_id?: string;
  shot_id?: string;
  /** purpose=shared_library 時に必須（対象セクションの編集権限が必要） */
  section_id?: string;
}

/** シングルアップロードのレスポンスデータ */
export interface SingleUploadData {
  upload_type: 'single';
  file_key: string;
  presigned_post: {
    url: string;
    fields: Record<string, string>;
  };
  /** 公開オブジェクト URL（purpose=task_board のときのみ） */
  file_url?: string;
}

/** マルチパートアップロードのレスポンスデータ */
export interface MultipartUploadData {
  upload_type: 'multipart';
  file_key: string;
  upload_id: string;
  part_size: number;
  part_urls: Array<{ part_number: number; url: string }>;
  /** 公開オブジェクト URL（purpose=task_board のときのみ） */
  file_url?: string;
}

/** アップロード開始レスポンス */
export interface InitiateUploadResponse {
  status: string;
  data: SingleUploadData | MultipartUploadData;
}

/** マルチパートアップロード完了リクエストパラメータ */
export interface CompleteUploadParams {
  file_key: string;
  upload_id: string;
  parts: Array<{ part_number: number; etag: string }>;
}

/** マルチパートアップロード完了レスポンス */
export interface CompleteUploadResponse {
  status: string;
  data: {
    file_key: string;
    location: string;
    etag: string;
  };
}

/** マルチパートアップロード中止リクエストパラメータ */
export interface AbortUploadParams {
  file_key: string;
  upload_id: string;
}

/** マルチパートアップロード中止レスポンス */
export interface AbortUploadResponse {
  status: string;
  data: {
    file_key: string;
    upload_id: string;
    status: string;
  };
}

/** ファイル登録リクエストパラメータ */
export interface RegisterUploadParams {
  file_key: string;
  purpose: UploadPurpose;
  project_id?: string;
  message_id?: string;
  folder_id?: string;
  /** フォルダパス（例: "親フォルダ/子フォルダ"）- フォルダアップロード時に使用 */
  folder_path?: string;
}

/** 登録済みファイル情報 */
export interface RegisteredFile {
  file_id: string;
  file_name: string;
  file_url: string;
  file_key: string;
  converted_url: string | null;
  waveform_url: string | null;
  thumbnail_url: string | null;
  conversion_status: string | null;
  is_hls: boolean;
}

/** ファイル登録レスポンス */
export interface RegisterUploadResponse {
  status: string;
  data: RegisteredFile;
}

/** エラーレスポンス */
export interface UploadErrorResponse {
  status: 'error';
  message: string;
}

// ===== API定義 =====

export const uploadApi = createApi({
  reducerPath: "uploadApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  endpoints: (builder) => ({
    /**
     * アップロード開始
     * ファイルサイズに応じてシングル/マルチパートアップロードのpresigned URLを取得
     */
    initiateUpload: builder.mutation<InitiateUploadResponse, InitiateUploadParams>({
      query: (params) => ({
        url: "/api/upload/initiate",
        method: "POST",
        body: params,
      }),
    }),

    /**
     * マルチパートアップロード完了
     * 全パートのアップロード完了後に呼び出し
     */
    completeUpload: builder.mutation<CompleteUploadResponse, CompleteUploadParams>({
      query: (params) => ({
        url: "/api/upload/complete",
        method: "POST",
        body: params,
      }),
    }),

    /**
     * マルチパートアップロード中止
     * エラー発生時やユーザーキャンセル時に呼び出し
     */
    abortUpload: builder.mutation<AbortUploadResponse, AbortUploadParams>({
      query: (params) => ({
        url: "/api/upload/abort",
        method: "POST",
        body: params,
      }),
    }),

    /**
     * ファイル登録
     * S3アップロード完了後、DBにメタデータを登録
     */
    registerUpload: builder.mutation<RegisterUploadResponse, RegisterUploadParams>({
      query: (params) => ({
        url: "/api/upload/register",
        method: "POST",
        body: params,
      }),
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useInitiateUploadMutation,
  useCompleteUploadMutation,
  useAbortUploadMutation,
  useRegisterUploadMutation,
} = uploadApi;
